// Universal Tricks Interactive Tutorials
// Double and Halve, 15% Tip, Estimating Square Roots

import { TechniqueBreakdown } from "../techniqueBreakdowns";

export const universalTricksTutorials: Record<string, TechniqueBreakdown> = {
  "e3f4a5b6-c7d8-9d0e-1f2a-dcedf0123456": {
    title: "Double and Halve Multiplication",
    description:
      "Simplify multiplication by doubling one number and halving the other repeatedly until you get easy numbers!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify Even Numbers",
        explanation:
          "Look for an even number to halve. Keep doubling the other number while halving the even one.",
        practice: {
          problem: "16 × 25",
          question: "Which number should we halve first?",
          answer: "16",
          hint: "16 is even, so we can halve it easily",
        },
      },
      {
        stepNumber: 2,
        title: "Halve and Double",
        explanation:
          "Halve one number, double the other. Repeat until you get simple numbers.",
        practice: {
          problem: "16 × 25",
          question: "After halving 16 and doubling 25, what do we get?",
          answer: "8 × 50",
          hint: "16÷2=8, 25×2=50",
          intermediateSteps: [
            { label: "Halve 16", answer: "8" },
            { label: "Double 25", answer: "50" },
            { label: "New problem", answer: "8 × 50" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Continue Until Simple",
        explanation:
          "Keep going until you reach numbers you can multiply mentally.",
        practice: {
          problem: "8 × 50",
          question: "Continue: halve 8, double 50. What do we get?",
          answer: "4 × 100",
          hint: "8÷2=4, 50×2=100",
          intermediateSteps: [
            { label: "Halve 8", answer: "4" },
            { label: "Double 50", answer: "100" },
            { label: "Final problem", answer: "4 × 100" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Calculate Final Answer",
        explanation: "Multiply the simplified numbers to get your answer.",
        practice: {
          problem: "4 × 100",
          question: "What is 4 × 100?",
          answer: "400",
          hint: "Easy multiplication!",
        },
      },
    ],
    finalExample: {
      problem: "16 × 25",
      fullSolution: "400 (16×25 → 8×50 → 4×100 = 400)",
    },
  },

  "f4a5b6c7-d8e9-0e1f-2a3b-cedf01234567": {
    title: "Quick 15% Tip Calculation",
    description:
      "Calculate 15% tips instantly: find 10%, then add half of that!",
    steps: [
      {
        stepNumber: 1,
        title: "Find 10% (Move Decimal)",
        explanation:
          "To find 10%, simply move the decimal point one place left.",
        practice: {
          problem: "Bill: $40",
          question: "What is 10% of $40?",
          answer: "4",
          hint: "Move decimal left: 40.0 → 4.0",
        },
      },
      {
        stepNumber: 2,
        title: "Find 5% (Half of 10%)",
        explanation: "5% is half of 10%. Divide the 10% amount by 2.",
        practice: {
          problem: "10% = $4",
          question: "What is 5% (half of $4)?",
          answer: "2",
          hint: "4 ÷ 2 = 2",
          intermediateSteps: [
            { label: "10% amount", answer: "$4" },
            { label: "Divide by 2", answer: "$4 ÷ 2 = $2" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Add 10% + 5% = 15%",
        explanation: "Add the 10% and 5% amounts together to get 15%.",
        practice: {
          problem: "10% = $4, 5% = $2",
          question: "What is the 15% tip?",
          answer: "6",
          hint: "$4 + $2 = $6",
          intermediateSteps: [
            { label: "10%", answer: "$4" },
            { label: "5%", answer: "$2" },
            { label: "15% tip", answer: "$6" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "$40 bill",
      fullSolution: "$6 tip (10%=$4, 5%=$2, Total=$6)",
    },
  },

  "a5b6c7d8-e9f0-1f2a-3b4c-edf012345678": {
    title: "Estimating Square Roots",
    description:
      "Estimate square roots of non-perfect squares by finding nearby perfect squares!",
    steps: [
      {
        stepNumber: 1,
        title: "Find Nearby Perfect Squares",
        explanation:
          "Identify the perfect squares just below and above your number.",
        practice: {
          problem: "√50",
          question: "What perfect squares are near 50? (below and above)",
          answer: "49 and 64",
          hint: "7²=49, 8²=64",
          intermediateSteps: [
            { label: "Below 50", answer: "49 (7²)" },
            { label: "Above 50", answer: "64 (8²)" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Determine Range",
        explanation:
          "The square root is between the roots of those perfect squares.",
        practice: {
          problem: "√50 (between 49 and 64)",
          question: "The answer is between which two integers?",
          answer: "7 and 8",
          hint: "√49=7, √64=8, so √50 is between 7 and 8",
        },
      },
      {
        stepNumber: 3,
        title: "Estimate Position",
        explanation:
          "See how close your number is to each perfect square to refine estimate.",
        practice: {
          problem: "√50 (closer to 49 or 64?)",
          question: "Is 50 closer to 49 or 64?",
          answer: "49",
          hint: "50-49=1, 64-50=14, so much closer to 49",
          intermediateSteps: [
            { label: "Distance to 49", answer: "50-49 = 1" },
            { label: "Distance to 64", answer: "64-50 = 14" },
            { label: "Closer to", answer: "49, so estimate ~7.1" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "√50",
      fullSolution: "~7.1 (between 7² and 8², closer to 7)",
    },
  },

  "b1a2c3d4-e5f6-a7b8-c9d0-e1f2a3b4c5d6": {
    title: "Dividing by 5, 50, 500",
    description:
      "Fast division by 5, 50, or 500 by doubling and moving the decimal!",
    steps: [
      {
        stepNumber: 1,
        title: "Double the Number",
        explanation:
          "First step is simple: multiply your number by 2 (double it).",
        practice: {
          problem: "140 ÷ 5",
          question: "What is 140 × 2?",
          answer: "280",
          hint: "Double 140: 140 × 2 = 280",
        },
      },
      {
        stepNumber: 2,
        title: "Move Decimal Based on Divisor",
        explanation:
          "For ÷5: move decimal 1 place left. For ÷50: move 2 places left. For ÷500: move 3 places left.",
        practice: {
          problem: "280 ÷ 10 (dividing by 5)",
          question:
            "Move decimal in 280.0 one place to the left. What do you get?",
          answer: "28",
          hint: "280.0 → 28.0 (move decimal 1 place left)",
          intermediateSteps: [
            { label: "Original doubled", answer: "280.0" },
            { label: "Move 1 place left", answer: "28.0" },
            { label: "Final answer", answer: "28" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Apply to Larger Divisors",
        explanation:
          "Same method works for 50 and 500! Just move the decimal more places.",
        practice: {
          problem: "3100 ÷ 50",
          question:
            "Double 3100, then move decimal 2 places left. What is the answer?",
          answer: "62",
          hint: "Double: 3100 × 2 = 6200. Move 2 places: 6200.0 → 62.00",
          intermediateSteps: [
            { label: "Double 3100", answer: "6200" },
            { label: "Move 2 places left", answer: "62.00" },
            { label: "Final answer", answer: "62" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "3100 ÷ 50",
      fullSolution: "62 (Double: 6200, Move decimal 2 left: 62)",
    },
  },

  "c2b3d4e5-f6a7-b8c9-d0e1-f2a3b4c5d6e7": {
    title: "Multiplication by Doubling (x4, x8)",
    description:
      "Multiply by 4 or 8 using repeated doubling - no memorization needed!",
    steps: [
      {
        stepNumber: 1,
        title: "Multiply by 4: Double Twice",
        explanation:
          "To multiply by 4, just double your number, then double the result.",
        practice: {
          problem: "18 × 4",
          question: "What is 18 doubled (18 × 2)?",
          answer: "36",
          hint: "18 × 2 = 36 (first double)",
        },
      },
      {
        stepNumber: 2,
        title: "Complete the x4",
        explanation: "Double the result from step 1 to get your final answer.",
        practice: {
          problem: "36 × 2",
          question: "What is 36 doubled?",
          answer: "72",
          hint: "36 × 2 = 72 (second double)",
          intermediateSteps: [
            { label: "First double (18×2)", answer: "36" },
            { label: "Second double (36×2)", answer: "72" },
            { label: "So 18 × 4", answer: "72" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Multiply by 8: Double Three Times",
        explanation: "For ×8, double three times total. Each double is easy!",
        practice: {
          problem: "13 × 8",
          question: "Double 13 three times. What is the final answer?",
          answer: "104",
          hint: "13 → 26 → 52 → 104",
          intermediateSteps: [
            { label: "First double (13×2)", answer: "26" },
            { label: "Second double (26×2)", answer: "52" },
            { label: "Third double (52×2)", answer: "104" },
            { label: "So 13 × 8", answer: "104" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "13 × 8",
      fullSolution: "104 (13 → 26 → 52 → 104)",
    },
  },

  "d3c4e5f6-a7b8-c9d0-e1f2-a3b4c5d6e7f8": {
    title: "9-Times Table Finger Trick",
    description: "Use your 10 fingers as a calculator for the 9-times table!",
    steps: [
      {
        stepNumber: 1,
        title: "Set Up Your Hands",
        explanation:
          "Hold up both hands with palms facing you. Number your fingers 1-10 from left to right.",
        practice: {
          problem: "9 × 4",
          question: "Which finger (counting from the left) will you bend down?",
          answer: "4",
          hint: "To multiply 9 × 4, bend down your 4th finger from the left",
        },
      },
      {
        stepNumber: 2,
        title: "Count Tens (Left Side)",
        explanation:
          "The fingers to the LEFT of your bent finger represent the tens digit.",
        practice: {
          problem: "9 × 4 (4th finger bent)",
          question: "How many fingers are to the left of your bent finger?",
          answer: "3",
          hint: "Fingers 1, 2, 3 are left of finger 4. That is 3 tens = 30",
          intermediateSteps: [
            { label: "Bent finger", answer: "4th finger" },
            { label: "Fingers on left", answer: "3 fingers" },
            { label: "Tens digit", answer: "3 (representing 30)" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Count Ones (Right Side)",
        explanation:
          "The fingers to the RIGHT of your bent finger represent the ones digit.",
        practice: {
          problem: "9 × 4 (4th finger bent)",
          question: "How many fingers are to the right of your bent finger?",
          answer: "6",
          hint: "Fingers 5, 6, 7, 8, 9, 10 are right of finger 4. That is 6 ones",
          intermediateSteps: [
            { label: "Fingers on right", answer: "6 fingers" },
            { label: "Ones digit", answer: "6" },
            { label: "Combined (3 tens + 6 ones)", answer: "36" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Try Another Example",
        explanation: "Practice makes perfect! Try 9 × 7.",
        practice: {
          problem: "9 × 7",
          question: "Bend down your 7th finger. What is 9 × 7?",
          answer: "63",
          hint: "Left of 7th: 6 fingers (60). Right of 7th: 3 fingers (3). Answer: 63",
          intermediateSteps: [
            { label: "Bent finger", answer: "7th finger" },
            { label: "Left fingers (tens)", answer: "6 (representing 60)" },
            { label: "Right fingers (ones)", answer: "3" },
            { label: "Final answer", answer: "63" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "9 × 7",
      fullSolution: "63 (Bend 7th finger: 6 left + 3 right = 63)",
    },
  },

  "e4a5b6c7-d8e9-f0a1-b2c3-d4e5f6a7b8c9": {
    title: "Multiplying by 21, 31, 41...",
    description:
      "Fast multiplication by numbers ending in 1 using the distributive property!",
    steps: [
      {
        stepNumber: 1,
        title: "Identify the Pattern",
        explanation:
          "Numbers like 21, 31, 41 are (20+1), (30+1), (40+1). We can break them apart!",
        practice: {
          problem: "45 × 21",
          question: "What is 21 written as (tens + 1)?",
          answer: "20 + 1",
          hint: "21 = 20 + 1, so we can do (45 × 20) + (45 × 1)",
        },
      },
      {
        stepNumber: 2,
        title: "Multiply by the Tens Part",
        explanation:
          "Multiply by 20, 30, 40, etc. by multiplying by 2, 3, 4, then adding a zero.",
        practice: {
          problem: "45 × 20",
          question: "What is 45 × 20?",
          answer: "900",
          hint: "45 × 2 = 90, then add a zero: 900",
          intermediateSteps: [
            { label: "First: 45 × 2", answer: "90" },
            { label: "Add zero for ×20", answer: "900" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Add the Original Number",
        explanation: 'The "×1" part is just adding the original number once!',
        practice: {
          problem: "900 + 45",
          question: "What is 900 + 45?",
          answer: "945",
          hint: "900 + 45 = 945. This is your final answer for 45 × 21!",
          intermediateSteps: [
            { label: "45 × 20", answer: "900" },
            { label: "45 × 1", answer: "45" },
            { label: "Add them", answer: "945" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Try with 31",
        explanation: "Same method works for any number ending in 1!",
        practice: {
          problem: "60 × 31",
          question: "What is (60 × 30) + 60?",
          answer: "1860",
          hint: "60 × 3 = 180, add zero: 1800. Then 1800 + 60 = 1860",
          intermediateSteps: [
            { label: "60 × 3", answer: "180" },
            { label: "Add zero (×30)", answer: "1800" },
            { label: "Add original (×1)", answer: "1800 + 60 = 1860" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "60 × 31",
      fullSolution: "1860 (60×30=1800, 1800+60=1860)",
    },
  },

  "c7d8e9f0-a1b2-3b4c-5d6e-f0123456789a": {
    title: "Multiply by 12",
    description:
      "Multiply by 12 easily: multiply by 10, then by 2, and add them together!",
    steps: [
      {
        stepNumber: 1,
        title: "Multiply by 10",
        explanation:
          "First, multiply your number by 10 (just add a zero at the end).",
        practice: {
          problem: "24 × 12",
          question: "What is 24 × 10?",
          answer: "240",
          hint: "To multiply by 10, add a zero: 24 → 240",
        },
      },
      {
        stepNumber: 2,
        title: "Multiply by 2",
        explanation: "Now multiply your original number by 2 (double it).",
        practice: {
          problem: "24 × 2",
          question: "What is 24 × 2?",
          answer: "48",
          hint: "Double 24: 24 × 2 = 48",
          intermediateSteps: [
            { label: "24 × 10", answer: "240" },
            { label: "24 × 2", answer: "48" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Add the Results",
        explanation:
          "Add the results from steps 1 and 2 to get your final answer. (10 + 2 = 12!)",
        practice: {
          problem: "240 + 48",
          question: "What is 240 + 48?",
          answer: "288",
          hint: "240 + 48 = 288. This is 24 × 12!",
          intermediateSteps: [
            { label: "24 × 10", answer: "240" },
            { label: "24 × 2", answer: "48" },
            { label: "Add together", answer: "288" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "24 × 12",
      fullSolution: "288 (24×10=240, 24×2=48, 240+48=288)",
    },
  },

  "dc4e8e02-9d26-4b1e-9a21-18ce7c01d59f": {
    title: "Reverse Percentage Trick",
    description:
      "Use the commutative property to make percentage calculations easier: 8% of 50 = 50% of 8!",
    steps: [
      {
        stepNumber: 1,
        title: "Understand the Trick",
        explanation:
          "Percentages are commutative: A% of B = B% of A. Choose the easier calculation!",
        practice: {
          problem: "8% of 50",
          question: "Instead of 8% of 50, what easier calculation can we do?",
          answer: "50% of 8",
          hint: "8% of 50 = 50% of 8. Which is easier? 50% of 8 is just half of 8!",
        },
      },
      {
        stepNumber: 2,
        title: "Calculate the Easier Way",
        explanation:
          "Now solve the reversed percentage which is usually much simpler.",
        practice: {
          problem: "50% of 8",
          question: "What is 50% of 8?",
          answer: "4",
          hint: "50% means half, so 8 ÷ 2 = 4",
          intermediateSteps: [
            { label: "Original problem", answer: "8% of 50" },
            { label: "Reverse to", answer: "50% of 8" },
            { label: "50% = half", answer: "8 ÷ 2 = 4" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Practice Another Example",
        explanation: "Try 4% of 75 using the reverse trick.",
        practice: {
          problem: "4% of 75",
          question: "What is the easier way to calculate 4% of 75?",
          answer: "75% of 4",
          hint: "Reverse it: 4% of 75 = 75% of 4",
          intermediateSteps: [
            { label: "Original", answer: "4% of 75" },
            { label: "Reverse to", answer: "75% of 4" },
            { label: "Calculate", answer: "3/4 of 4 = 3" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "When to Use This Trick",
        explanation:
          "Use this when one number is easy to find a percentage of (like 4, 8, 10, 50, 100).",
        practice: {
          problem: "16% of 25",
          question: "What is 25% of 16?",
          answer: "4",
          hint: "25% = 1/4, so 16 ÷ 4 = 4. Same as 16% of 25!",
          intermediateSteps: [
            { label: "Reverse", answer: "25% of 16" },
            { label: "25% = 1/4", answer: "16 ÷ 4" },
            { label: "Final answer", answer: "4" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "16% of 25",
      fullSolution: "4 (Reverse to: 25% of 16 = 16÷4 = 4)",
    },
  },

  "e1a2b3c4-d5e6-f7a8-b9c0-d1e2f3a4b5c6": {
    title: "Divisibility Rules",
    description:
      "Quick mental checks to see if a number divides evenly - no calculator needed!",
    steps: [
      {
        stepNumber: 1,
        title: "Rule for 2 (Even Numbers)",
        explanation:
          "A number is divisible by 2 if its last digit is even (0, 2, 4, 6, 8).",
        practice: {
          problem: "Is 2,846 divisible by 2?",
          question: "What is the last digit?",
          answer: "6",
          hint: "Last digit is 6 (even), so YES, divisible by 2!",
          intermediateSteps: [
            { label: "Last digit", answer: "6" },
            { label: "Is 6 even?", answer: "Yes" },
            { label: "Divisible by 2?", answer: "Yes ✓" },
          ],
        },
      },
      {
        stepNumber: 2,
        title: "Rule for 3 (Sum of Digits)",
        explanation:
          "A number is divisible by 3 if the sum of its digits is divisible by 3.",
        practice: {
          problem: "Is 381 divisible by 3?",
          question: "Add all digits: 3 + 8 + 1 = ?",
          answer: "12",
          hint: "3+8+1=12. Is 12 divisible by 3? Yes (12÷3=4), so 381 is divisible by 3!",
          intermediateSteps: [
            { label: "Add digits", answer: "3 + 8 + 1 = 12" },
            { label: "Is 12 ÷ 3 even?", answer: "Yes (12÷3=4)" },
            { label: "Divisible by 3?", answer: "Yes ✓" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Rule for 5 (Ends in 0 or 5)",
        explanation: "A number is divisible by 5 if it ends in 0 or 5.",
        practice: {
          problem: "Is 4,275 divisible by 5?",
          question: "What is the last digit?",
          answer: "5",
          hint: "Last digit is 5, so YES, divisible by 5!",
          intermediateSteps: [
            { label: "Last digit", answer: "5" },
            { label: "Is it 0 or 5?", answer: "Yes (5)" },
            { label: "Divisible by 5?", answer: "Yes ✓" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Rule for 9 (Sum of Digits)",
        explanation:
          "A number is divisible by 9 if the sum of its digits is divisible by 9.",
        practice: {
          problem: "Is 729 divisible by 9?",
          question: "Add all digits: 7 + 2 + 9 = ?",
          answer: "18",
          hint: "7+2+9=18. Is 18 divisible by 9? Yes (18÷9=2), so 729 is divisible by 9!",
          intermediateSteps: [
            { label: "Add digits", answer: "7 + 2 + 9 = 18" },
            { label: "Is 18 ÷ 9 even?", answer: "Yes (18÷9=2)" },
            { label: "Divisible by 9?", answer: "Yes ✓" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Rule for 10 (Ends in 0)",
        explanation: "A number is divisible by 10 if it ends in 0.",
        practice: {
          problem: "Is 3,560 divisible by 10?",
          question: "What is the last digit?",
          answer: "0",
          hint: "Last digit is 0, so YES, divisible by 10!",
          intermediateSteps: [
            { label: "Last digit", answer: "0" },
            { label: "Divisible by 10?", answer: "Yes ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Is 540 divisible by 2, 3, 5, 9, 10?",
      fullSolution:
        "2✓(ends 0), 3✓(5+4+0=9, 9÷3=3), 5✓(ends 0), 9✓(sum=9), 10✓(ends 0)",
    },
  },

  "a1b2c3d4-e5f6-7a8b-9c0d-1e2f3a4b5c6d": {
    title: "Multiply by 25",
    description: "Lightning-fast multiplication by 25 using division by 4!",
    steps: [
      {
        stepNumber: 1,
        title: "Understanding the Trick",
        explanation:
          "25 is exactly 1/4 of 100. So multiplying by 25 is the same as dividing by 4 and then multiplying by 100!",
        practice: {
          problem: "What is 100 ÷ 4?",
          question: "Calculate this to see why 25 is special",
          answer: "25",
          hint: "100 ÷ 4 = 25. So 25 = 100/4!",
        },
      },
      {
        stepNumber: 2,
        title: "Step 1: Divide by 4",
        explanation:
          "First, divide the number by 4. This is easier than multiplying by 25 directly!",
        practice: {
          problem: "36 × 25",
          question: "What is 36 ÷ 4?",
          answer: "9",
          hint: "36 divided by 4 equals 9",
        },
      },
      {
        stepNumber: 3,
        title: "Step 2: Multiply by 100",
        explanation:
          "After dividing by 4, multiply by 100 (just add two zeros).",
        practice: {
          problem: "9 × 100",
          question: "What is 9 × 100?",
          answer: "900",
          hint: "Add two zeros to 9: 900",
          intermediateSteps: [
            { label: "From step 2", answer: "36 ÷ 4 = 9" },
            { label: "Multiply by 100", answer: "900" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Try a Larger Number",
        explanation: "The same method works for any number divisible by 4!",
        practice: {
          problem: "64 × 25",
          question: "What is the answer?",
          answer: "1600",
          hint: "64 ÷ 4 = 16, then 16 × 100 = 1600",
          intermediateSteps: [
            { label: "Divide by 4", answer: "64 ÷ 4 = 16" },
            { label: "Multiply by 100", answer: "16 × 100 = 1600" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "64 × 25",
      fullSolution: "1600 (64÷4=16, 16×100=1600)",
    },
  },

  "b6c7d8e9-f0a1-2a3b-4c5d-df0123456789": {
    title: "Subtracting by Adding Up",
    description:
      "Subtraction by finding the difference incrementally - just like giving change!",
    steps: [
      {
        stepNumber: 1,
        title: "Think Like a Cashier",
        explanation:
          "Instead of subtracting, add up from the smaller number to the larger number in easy steps.",
        practice: {
          problem: "1000 - 738",
          question:
            "Starting at 738, how much to get to the next easy number (740)?",
          answer: "2",
          hint: "738 + 2 = 740",
        },
      },
      {
        stepNumber: 2,
        title: "Jump to Round Numbers",
        explanation:
          "Keep adding to reach round numbers (like 800, then 1000).",
        practice: {
          problem: "From 740, reach 800",
          question: "How much do we add to 740 to get to 800?",
          answer: "60",
          hint: "740 + 60 = 800",
          intermediateSteps: [
            { label: "First jump", answer: "738 → 740 (+2)" },
            { label: "Second jump", answer: "740 → 800 (+60)" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Final Jump",
        explanation: "Make the last jump to your target number.",
        practice: {
          problem: "From 800, reach 1000",
          question: "How much do we add to 800 to get to 1000?",
          answer: "200",
          hint: "800 + 200 = 1000",
          intermediateSteps: [
            { label: "All jumps", answer: "+2, +60, +200" },
            { label: "Path", answer: "738 → 740 → 800 → 1000" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Add Up All Jumps",
        explanation:
          "The sum of all your jumps is the answer to the subtraction!",
        practice: {
          problem: "1000 - 738",
          question: "What is 2 + 60 + 200?",
          answer: "262",
          hint: "Add the jumps: 2 + 60 + 200 = 262",
          intermediateSteps: [
            { label: "Jump 1", answer: "+2" },
            { label: "Jump 2", answer: "+60" },
            { label: "Jump 3", answer: "+200" },
            { label: "Total", answer: "262" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "1000 - 738",
      fullSolution: "262 (738→740→800→1000: 2+60+200=262)",
    },
  },

  "a0b1c2d3-e4f5-6a7b-8c9d-ef0123456789": {
    title: "Quick Temperature Conversion (C to F)",
    description:
      "Convert Celsius to Fahrenheit quickly: double it, subtract 10%, then add 32!",
    steps: [
      {
        stepNumber: 1,
        title: "Double the Celsius",
        explanation: "First step: multiply the Celsius temperature by 2.",
        practice: {
          problem: "20°C to °F",
          question: "What is 20 × 2?",
          answer: "40",
          hint: "Double 20: 20 × 2 = 40",
        },
      },
      {
        stepNumber: 2,
        title: "Subtract 10%",
        explanation: "Subtract 10% of the doubled number (or 1/10 of it).",
        practice: {
          problem: "40 - 10% of 40",
          question: "What is 10% of 40?",
          answer: "4",
          hint: "10% of 40 is 4. So 40 - 4 = 36",
          intermediateSteps: [
            { label: "Doubled", answer: "40" },
            { label: "10% of 40", answer: "4" },
            { label: "Subtract", answer: "40 - 4 = 36" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Add 32",
        explanation: "Add 32 to get your Fahrenheit temperature!",
        practice: {
          problem: "36 + 32",
          question: "What is the final temperature in Fahrenheit?",
          answer: "68",
          hint: "36 + 32 = 68°F",
          intermediateSteps: [
            { label: "After step 2", answer: "36" },
            { label: "Add 32", answer: "36 + 32 = 68°F" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Try Another Example",
        explanation: "Practice with 30°C!",
        practice: {
          problem: "30°C to °F",
          question: "What is the answer?",
          answer: "86",
          hint: "30×2=60, 60-6=54, 54+32=86°F",
          intermediateSteps: [
            { label: "Double", answer: "30 × 2 = 60" },
            { label: "Subtract 10%", answer: "60 - 6 = 54" },
            { label: "Add 32", answer: "54 + 32 = 86°F" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "30°C",
      fullSolution: "86°F (30×2=60, 60-6=54, 54+32=86)",
    },
  },

  "e9f0a1b2-c3d4-5d6e-7f8a-123456789abc": {
    title: "Quick Conversion: Kilometers to Miles",
    description:
      "Use the Fibonacci sequence to convert kilometers to miles - like magic!",
    steps: [
      {
        stepNumber: 1,
        title: "Learn the Fibonacci Sequence",
        explanation:
          "Fibonacci sequence: each number is the sum of the previous two: 1, 1, 2, 3, 5, 8, 13, 21, 34, 55, 89...",
        practice: {
          problem: "What comes after 5, 8, 13 in Fibonacci?",
          question: "Add the last two numbers: 8 + 13 = ?",
          answer: "21",
          hint: "8 + 13 = 21. The sequence continues: ...5, 8, 13, 21, 34...",
        },
      },
      {
        stepNumber: 2,
        title: "The Magic Trick",
        explanation:
          "To convert km to miles, find your number in the Fibonacci sequence and the PREVIOUS number is approximately the miles!",
        practice: {
          problem: "Convert 21 km to miles",
          question:
            "Find 21 in the sequence (1,1,2,3,5,8,13,21,34...). What number comes BEFORE 21?",
          answer: "13",
          hint: "The sequence is ...8, 13, 21... so the number before 21 is 13 miles!",
          intermediateSteps: [
            { label: "Find in sequence", answer: "...8, 13, 21, 34..." },
            { label: "Previous number", answer: "13" },
            { label: "Answer", answer: "21 km ≈ 13 miles" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Going the Other Way (Miles to Km)",
        explanation:
          "To convert miles to km, find your number in Fibonacci and the NEXT number is approximately the km!",
        practice: {
          problem: "Convert 13 miles to km",
          question: "Find 13 in the sequence. What number comes AFTER 13?",
          answer: "21",
          hint: "The sequence is ...8, 13, 21... so the number after 13 is 21 km!",
          intermediateSteps: [
            { label: "Find in sequence", answer: "...8, 13, 21, 34..." },
            { label: "Next number", answer: "21" },
            { label: "Answer", answer: "13 miles ≈ 21 km" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Why Does This Work?",
        explanation:
          "The ratio between consecutive Fibonacci numbers approaches 1.618 (the golden ratio), which is very close to 1.609 (the actual km/mile ratio)!",
        practice: {
          problem: "Convert 55 km to miles",
          question: "What is the Fibonacci number before 55?",
          answer: "34",
          hint: "Sequence: ...21, 34, 55, 89... so 55 km ≈ 34 miles",
          intermediateSteps: [
            { label: "Sequence", answer: "...21, 34, 55, 89..." },
            { label: "Previous", answer: "34" },
            { label: "Answer", answer: "55 km ≈ 34 miles ✓" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "89 km to miles",
      fullSolution: "55 miles (Fibonacci: ...34, 55, 89, 144...)",
    },
  },

  "f4b1e2d3-c8a7-4b6e-9f0d-1e2c3b4a5d6e": {
    title: "Digit Sums (Casting out Nines)",
    description:
      "Check arithmetic by adding digits repeatedly until you get a single digit!",
    steps: [
      {
        stepNumber: 1,
        title: "Finding a Digit Sum",
        explanation:
          "Add all the digits in a number. If the result has multiple digits, add them again until you get a single digit.",
        practice: {
          problem: "47",
          question: "What is 4 + 7?",
          answer: "11",
          hint: "Add the digits: 4 + 7 = 11",
        },
      },
      {
        stepNumber: 2,
        title: "Reduce to Single Digit",
        explanation:
          "When you get a multi-digit sum, keep adding the digits until you have just one digit.",
        practice: {
          problem: "11 (from step 1)",
          question: "What is 1 + 1?",
          answer: "2",
          hint: "Add the digits of 11: 1 + 1 = 2",
          intermediateSteps: [
            { label: "47 → digit sum", answer: "4 + 7 = 11" },
            { label: "11 → reduce", answer: "1 + 1 = 2" },
            { label: "Final digit sum", answer: "2" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Check Addition",
        explanation:
          "For addition: find digit sum of each number, add those sums, and compare with the answer's digit sum.",
        practice: {
          problem: "Check: 47 + 38 = 85",
          question:
            "Digit sum of 47 is 2 (from earlier). What is digit sum of 38?",
          answer: "11",
          hint: "3 + 8 = 11, then 1 + 1 = 2",
          intermediateSteps: [
            { label: "DS(47)", answer: "2" },
            { label: "DS(38)", answer: "3+8=11→2" },
            { label: "Add digit sums", answer: "2 + 2 = 4" },
            { label: "DS(85)", answer: "8+5=13→4" },
            { label: "Match?", answer: "Yes! ✓" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Detect Errors",
        explanation: "If digit sums don't match, you made an error!",
        practice: {
          problem: "Check: 56 + 29 = 86 (wrong!)",
          question:
            "DS(56)=11→2, DS(29)=11→2. Sum=4. DS(86)=14→5. Do they match?",
          answer: "no",
          hint: "4 ≠ 5, so there's an error! (Correct answer is 85)",
          intermediateSteps: [
            { label: "DS(56)+DS(29)", answer: "2 + 2 = 4" },
            { label: "DS(86)", answer: "8+6=14→5" },
            { label: "Match?", answer: "No! Error detected ✗" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "Check: 47 + 38 = 85",
      fullSolution: "Correct! DS(47)=2, DS(38)=2, Sum=4, DS(85)=4 ✓",
    },
  },

  "f5a6b7c8-d9e0-1f2a-3b4c-5d6e7f8a9b0c": {
    title: "Doubling: Multiply by 2, 4, 8",
    description:
      "Master multiplication by powers of 2 using simple repeated doubling!",
    steps: [
      {
        stepNumber: 1,
        title: "The Power of Doubling",
        explanation:
          "Doubling (×2) is one of the easiest mental operations. You can multiply by any power of 2 by doubling repeatedly!",
        practice: {
          problem: "75 × 2",
          question: "What is 75 doubled?",
          answer: "150",
          hint: "75 + 75 = 150, or think: 70×2=140, 5×2=10, total=150",
        },
      },
      {
        stepNumber: 2,
        title: "Multiply by 4 = Double Twice",
        explanation:
          "To multiply by 4, just double your number, then double the result. That's it!",
        practice: {
          problem: "75 × 4",
          question:
            "You already know 75×2=150. Now double 150. What do you get?",
          answer: "300",
          hint: "150 × 2 = 300",
          intermediateSteps: [
            { label: "Step 1: Double 75", answer: "150" },
            { label: "Step 2: Double 150", answer: "300" },
            { label: "So 75 × 4", answer: "300" },
          ],
        },
      },
      {
        stepNumber: 3,
        title: "Multiply by 8 = Double Three Times",
        explanation:
          "For ×8, double three times. Each step is simple doubling!",
        practice: {
          problem: "13 × 8",
          question: "Double 13 three times. What is the final answer?",
          answer: "104",
          hint: "First: 13×2=26. Second: 26×2=52. Third: 52×2=104",
          intermediateSteps: [
            { label: "First double (13×2)", answer: "26" },
            { label: "Second double (26×2)", answer: "52" },
            { label: "Third double (52×2)", answer: "104" },
            { label: "So 13 × 8", answer: "104" },
          ],
        },
      },
      {
        stepNumber: 4,
        title: "Why Does This Work?",
        explanation:
          "4 is really 2×2, and 8 is 2×2×2. We're just breaking multiplication into easier steps!",
        practice: {
          problem: "25 × 4",
          question: "Use the doubling method. What is 25 × 4?",
          answer: "100",
          hint: "Step 1: 25×2=50. Step 2: 50×2=100",
          intermediateSteps: [
            { label: "Understanding", answer: "4 = 2 × 2" },
            { label: "First double", answer: "25 × 2 = 50" },
            { label: "Second double", answer: "50 × 2 = 100" },
            { label: "Final answer", answer: "100" },
          ],
        },
      },
      {
        stepNumber: 5,
        title: "Advanced: Multiply by 16",
        explanation:
          "The same pattern continues! 16 = 2×2×2×2, so double four times.",
        practice: {
          problem: "17 × 16",
          question: "Double 17 four times. What is the answer?",
          answer: "272",
          hint: "17 → 34 → 68 → 136 → 272",
          intermediateSteps: [
            { label: "1st double", answer: "17 → 34" },
            { label: "2nd double", answer: "34 → 68" },
            { label: "3rd double", answer: "68 → 136" },
            { label: "4th double", answer: "136 → 272" },
            { label: "So 17 × 16", answer: "272" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "13 × 8",
      fullSolution: "104 (13 → 26 → 52 → 104, three doubles)",
    },
  },
  "6fb3465e-0170-4468-80e6-ccd36354d50c": {
    steps: [
      {
        title: "Step 1: Check if Even or Odd",
        explanation:
          "First, look at the number you're multiplying by 5. Is it even (ends in 0,2,4,6,8) or odd (ends in 1,3,5,7,9)? The method is slightly different for each.",
        practice: {
          problem: "84 × 5",
          question: "Is 84 even or odd?",
          answer: "even",
          hint: "84 ends in 4, which is even",
        },
      },
      {
        title: "Step 2: Divide by 2 (Even Numbers)",
        explanation:
          "For even numbers, divide the number by 2, then add a 0 at the end. That's your answer!",
        practice: {
          problem: "84 × 5",
          question: "What is 84 ÷ 2, then add a 0?",
          answer: "420",
          hint: "84 ÷ 2 = 42, add 0 → 420",
          intermediateSteps: [
            { label: "84 ÷ 2", answer: "42", hint: "Half of 84" },
            { label: "Add 0", answer: "420", hint: "Append a zero to the end" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "84 × 5",
      fullSolution: "420",
    },
  },

  "Multiply by 5 (Odd)": {
    steps: [
      {
        title: "Step 1: Check if Even or Odd",
        explanation:
          "First, look at the number you're multiplying by 5. Is it even (ends in 0,2,4,6,8) or odd (ends in 1,3,5,7,9)? The method is slightly different for each.",
        practice: {
          problem: "67 × 5",
          question: "Is 67 even or odd?",
          answer: "odd",
          hint: "67 ends in 7, which is odd",
        },
      },
      {
        title: "Step 2: Subtract 1 and Divide (Odd Numbers)",
        explanation:
          "For odd numbers, subtract 1 first, then divide by 2, then add a 5 at the end. That's your answer!",
        practice: {
          problem: "67 × 5",
          question: "What is (67 - 1) ÷ 2, then add a 5?",
          answer: "335",
          hint: "67 - 1 = 66, 66 ÷ 2 = 33, add 5 → 335",
          intermediateSteps: [
            {
              label: "67 - 1",
              answer: "66",
              hint: "Subtract 1 from odd number",
            },
            { label: "66 ÷ 2", answer: "33", hint: "Divide by 2" },
            { label: "Add 5", answer: "335", hint: "Append 5 to the end" },
          ],
        },
      },
    ],
    finalExample: {
      problem: "67 × 5",
      fullSolution: "335",
    },
  },

  "30541b58-166a-4052-a052-a6a02d40e2e7": {
    steps: [
      {
        title: "Step 1: Identify the First Digit(s)",
        explanation:
          "For a number ending in 5, identify all the digits BEFORE the 5. For 35, that's 3. For 75, that's 7. For 125, that's 12.",
        practice: {
          problem: "35²",
          question: "What digit(s) come before the 5 in 35?",
          answer: "3",
          hint: "The number before 5 is just 3",
        },
      },
      {
        title: "Step 2: Multiply by Next Number",
        explanation:
          "Take that first part and multiply it by the next consecutive number. So for 3, multiply by 4 (3 × 4 = 12).",
        practice: {
          problem: "35²",
          question: "What is 3 × 4?",
          answer: "12",
          hint: "3 times the next number (4) equals 12",
        },
      },
      {
        title: "Step 3: Append 25",
        explanation:
          'Finally, append "25" to the end of your result from Step 2. That\'s your answer!',
        practice: {
          problem: "35²",
          question: "Append 25 to 12. What do you get?",
          answer: "1225",
          hint: "Just write 25 after 12: 1225",
        },
      },
    ],
    finalExample: {
      problem: "35²",
      fullSolution: "1225",
    },
  },
};
